import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Edit } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import Field from './Fields/Field';
import { partialUpdateTramite } from '../services/tramiteService';
import { useAlert } from '../hooks/useAlert';
import { DialogClose } from '@radix-ui/react-dialog';
import { useAuth } from '@/auth';
import { checkAllPermissions, PERMISSIONS } from '@/auth/permissions';
import { formatDateLocale } from '@/common/utils/date';

const FechaEscrituraModal = ({ tramite, onChange, variant = 'default' }) => {
  const padDatePart = (value) => String(value).padStart(2, '0');

  const formatDateTimeLocal = (value) => {
    if (!value) return '';

    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) return '';
      return `${value.getFullYear()}-${padDatePart(value.getMonth() + 1)}-${padDatePart(value.getDate())}T${padDatePart(value.getHours())}:${padDatePart(value.getMinutes())}`;
    }

    if (typeof value === 'string') {
      const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(value);
      const localMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/);

      if (localMatch && !hasTimezone) {
        const [, year, month, day, hours = '00', minutes = '00'] = localMatch;
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      }

      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return '';

      return `${parsed.getFullYear()}-${padDatePart(parsed.getMonth() + 1)}-${padDatePart(parsed.getDate())}T${padDatePart(parsed.getHours())}:${padDatePart(parsed.getMinutes())}`;
    }

    return '';
  };

  // Helper to parse date strings/objects as local date
  const parseISOAsLocal = (dateInput) => {
    if (!dateInput) return null;

    // Si ya es un objeto Date válido, retornarlo
    if (dateInput instanceof Date && !isNaN(dateInput.getTime())) {
      return dateInput;
    }

    // Si es string, intentar parsear
    if (typeof dateInput === 'string') {
      // Intentar formato ISO (YYYY-MM-DD)
      const isoMatch = dateInput.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) {
        const [, year, month, day] = isoMatch;
        return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
      }

      // Intentar parseo genérico
      const parsed = new Date(dateInput);
      if (!isNaN(parsed.getTime())) {
        // Crear fecha local a partir de la fecha parseada
        return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
      }
    }

    return null;
  };

  const [fecescritura, setFecescritura] = useState(tramite.fecescritura ? parseISOAsLocal(tramite.fecescritura) : (() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
  })());
  const [fecfirma, setFecfirma] = useState(formatDateTimeLocal(tramite.fecfirma));
  const [numEscritura, setNumEscritura] = useState(tramite.num_escritura || '');
  const [numEntrada, setNumEntrada] = useState(tramite.num_entrada || '');
  const [fechaEntrada, setFechaEntrada] = useState(tramite.fecentrada ? parseISOAsLocal(tramite.fecentrada) : null);
  const { errorAlert, successAlert } = useAlert();
  const { user } = useAuth();
  const userPermissions = user?.permissions ?? [];
  const canManageTramites = checkAllPermissions(userPermissions, [PERMISSIONS.MANAGE_TRAMITES.name]);

  // Función para resetear el estado local a los valores del trámite
  const resetFormState = useCallback(() => {
    const parsedDate = tramite.fecescritura ? parseISOAsLocal(tramite.fecescritura) : (() => {
      const today = new Date();
      return new Date(today.getFullYear(), today.getMonth(), today.getDate());
    })();
    setFecescritura(parsedDate);
    setFecfirma(formatDateTimeLocal(tramite.fecfirma));
    setNumEscritura(tramite.num_escritura || '');
    setNumEntrada(tramite.num_entrada || '');
    setFechaEntrada(tramite.fecentrada ? parseISOAsLocal(tramite.fecentrada) : null);
  }, [tramite.fecescritura, tramite.fecfirma, tramite.num_escritura, tramite.num_entrada, tramite.fecentrada]);

  // Sincronizar estado local con props cuando cambia el trámite
  useEffect(() => {
    resetFormState();
  }, [tramite.id, resetFormState]);

  const formatDateToISO = (date) => {
    if (!date) return null;
    // Use local time methods
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSave = () => {
    partialUpdateTramite({
      id: tramite.id,
      fecfirma: fecfirma || null,
      fecescritura: formatDateToISO(fecescritura),
      num_escritura: numEscritura,
      num_entrada: numEntrada,
      fecentrada: formatDateToISO(fechaEntrada)
    })
      .then(r => {
        successAlert({ message: 'Fecha de escritura actualizada con éxito.' });
        onChange && onChange(r);
      }).catch(err => {
        errorAlert({
          message: 'Error al actualizar fecha.',
          exception: err
        });
      });
  }

  const renderTrigger = () => {
    if (variant === 'button') {
      return canManageTramites ? (
        <Button
          variant="ghost"
          size="sm"
          className="h-6 p-1 text-gray-600 hover:text-gray-700 hover:bg-gray-100"
        >
          <Edit className="w-3 h-3" />
          <span className="sr-only">Editar fecha de escritura</span>
        </Button>
      ) : null;
    }

    // Variant por defecto (legacy)
    return canManageTramites ? (
      <button className="flex items-center w-full text-xs sm:text-sm cursor-pointer hover:underline transition-colors">
        <Calendar className="mx-1 sm:mx-2 flex-shrink-0" size={14} />
        <span className="truncate">
          {tramite.fecescritura
            ? `Fecha escritura: ${formatDateLocale(tramite.fecescritura)}`
            : 'Fecha escritura: No establecida'
          }
        </span>
        <span className="sr-only"> - Editar fecha de escritura</span>
        <span className="ml-2">
          <Edit className="opacity-50 hover:opacity-100" size={14} />
        </span>
      </button>
    ) : (
      <div className="flex items-center w-full text-xs sm:text-sm">
        <Calendar className="mx-1 sm:mx-2 flex-shrink-0" size={14} />
        <span className="truncate">
          {tramite.fecescritura
            ? `Fecha escritura: ${formatDateLocale(tramite.fecescritura)}`
            : 'Fecha escritura: No establecida'
          }
        </span>
      </div>
    );
  };

  const handleOpenChange = (open) => {
    // Cuando el modal se abre, resetear el formulario a los valores actuales del trámite
    if (open) {
      resetFormState();
    }
  };

  return (
    <Dialog onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {renderTrigger()}
      </DialogTrigger>
      <DialogContent className="flex flex-col gap-4 sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Editar fecha de escritura</DialogTitle>
          <DialogDescription>
            Actualice la fecha de escritura y los datos relacionados del trámite.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field
            type="datetime-local"
            name="fecfirma"
            label="Fecha y hora estimada de firma"
            value={fecfirma}
            onChange={({ value }) => setFecfirma(value)}
          />
          {/* @ts-ignore */}
          <Field
            type="date"
            name="fecescritura"
            label="Fecha de escritura"
            value={fecescritura}
            onChange={({ value }) => setFecescritura(value)}
            inModal={true}
          />
          <Field
            type="text"
            name="num_escritura"
            label="# Escritura"
            value={numEscritura}
            onChange={({ value }) => setNumEscritura(value)}
          />
          <Field
            type="text"
            name="num_entrada"
            label="# Entrada"
            value={numEntrada}
            onChange={({ value }) => setNumEntrada(value)}
          />
          {/* @ts-ignore */}
          <Field
            type="date"
            name="fecentrada"
            label="Fecha de entrada"
            value={fechaEntrada}
            onChange={({ value }) => setFechaEntrada(value)}
            inModal={true}
          />
        </div>
        <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0">
          <DialogClose asChild>
            <Button variant="outline" className="order-2 sm:order-1">
              Cancelar
            </Button>
          </DialogClose>
          <Button onClick={handleSave} className="order-1 sm:order-2">
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default FechaEscrituraModal;
